import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown } from "lucide-react";
import StarField from "@/components/StarField";

const FRAMES = [
  { kicker: "01 / ARCHITECTURE", line: "DESIGNING SPACES." },
  { kicker: "02 / ENGINEERING", line: "ENGINEERING FUTURES." },
  { kicker: "03 / BIM", line: "ZERO-CLASH DELIVERY." },
  { kicker: "04 / VISUALIZATION", line: "SEE IT BEFORE IT'S BUILT." },
];
const FRAME_MS = 3200;

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % FRAMES.length), FRAME_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="top" className="relative h-screen min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div
        className="absolute inset-0"
        animate={{ scale: [1, 1.04] }}
        transition={{ duration: 45, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
      >
        <StarField />
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />

      <motion.img
        src="/star.png"
        alt=""
        initial={{ opacity: 0, rotate: -12, scale: 0.9 }}
        animate={{ opacity: 0.14, rotate: 0, scale: 1 }}
        transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute -right-32 -bottom-40 w-[46rem] pointer-events-none select-none"
      />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-28">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="font-mono-tech text-xs tracking-[0.35em] uppercase text-neutral-400 mb-6"
          data-testid="hero-overline"
        >
          Starscape Studio — Integrated Design + Engineering Consultancy
        </motion.p>

        <div className="min-h-[9rem] md:min-h-[12rem]" data-testid="hero-kinetic-frame">
          <AnimatePresence mode="wait">
            <motion.div key={idx}>
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.5 }}
                className="font-mono-tech text-xs tracking-[0.3em] uppercase text-neutral-500 mb-4"
                data-testid="hero-frame-kicker"
              >
                {FRAMES[idx].kicker}
              </motion.p>
              <div className="overflow-hidden">
                <motion.h1
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "-110%" }}
                  transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
                  className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-8xl leading-[0.95]"
                  data-testid="hero-frame-title"
                >
                  {FRAMES[idx].line}
                </motion.h1>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-6 max-w-md text-sm text-neutral-400 font-light leading-relaxed"
          data-testid="hero-subtext"
        >
          We bridge creative architectural vision and actionable construction technology — AI-ready, tech-driven, data-rich, coordinated.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-10 flex items-center gap-3"
          data-testid="hero-frame-progress"
        >
          {FRAMES.map((_, i) => (
            <div key={i} className="h-px w-14 bg-white/15 overflow-hidden">
              {i === idx && (
                <motion.div
                  key={idx}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: FRAME_MS / 1000, ease: "linear" }}
                  className="h-full bg-white origin-left"
                />
              )}
              {i < idx && <div className="h-full w-full bg-white/40" />}
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 right-8 lg:right-12 z-10 flex items-center gap-2 text-neutral-500"
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown size={14} className="animate-scroll-cue" />
      </motion.div>
    </section>
  );
}
